import type {
  AssignmentMapSnapshot,
  AssignmentSummary,
  DoorPoint,
  HouseConditionStatus,
  RouteMapFeature
} from '../domain/types';
import { addReadableStreetBasemap } from './carto-basemap';
import { makeMapPageFriendly } from './page-friendly-map';
import { mapZoom, mapZoomBand, zoomInterpolate } from './zoom-presentation';

declare const L: any;

const ROUTE_COLOR = '#0b69a3';
const COMPLETE_COLOR = '#2f7d4a';
const OTHER_ROUTE_COLOR = '#7b8a97';
const DOOR_COLOR = '#2f7d4a';
const REPORTED_DOOR_COLOR = '#d6a619';
const START_COLOR = '#2f7d4a';
const FALLBACK_CENTER: [number, number] = [33.437, -112.476];

export interface CoverageMapOptions {
  activeRouteId?: string;
  completedRouteIds?: string[];
  sectionLabels?: Record<string, string>;
  onReportDoor?: (door: DoorPoint, conditionStatus: HouseConditionStatus | null) => Promise<void> | void;
}

export function mountAssignmentMap(assignment: AssignmentSummary): void {
  mountCoverageMap('assignment-map', assignment.map, {
    completedRouteIds: assignment.sections
      .filter((section) => section.status === 'COMPLETE')
      .map((section) => section.id),
    ...(assignment.activeSectionId ? { activeRouteId: assignment.activeSectionId } : {}),
    sectionLabels: Object.fromEntries(assignment.sections.map((section) => [section.id, `Section ${section.sequence}`]))
  });
}

export function mountCoverageMap(
  elementId: string,
  snapshot: AssignmentMapSnapshot,
  options: CoverageMapOptions = {}
): void {
  const element = document.querySelector<HTMLDivElement>(`#${CSS.escape(elementId)}`);
  if (!element) return;

  const routeFeatures = snapshot.routes.features.filter(hasValidGeometry);
  const doors = snapshot.doors.filter(isValidDoor);
  const start = isValidMapPoint(snapshot.start) ? snapshot.start : undefined;
  const center = isValidMapPoint(snapshot.center) ? snapshot.center : undefined;
  const fallback = center ?? start;

  if (typeof L === 'undefined') {
    showMapFallback(
      element,
      'The map library could not load.',
      'Check the internet connection and refresh this page. The assignment itself is still safely claimed.'
    );
    return;
  }

  if (!routeFeatures.length && !doors.length && !fallback) {
    showMapFallback(
      element,
      'Map data is not available for this section yet.',
      'The assignment is saved, but this section still needs GIS geometry.'
    );
    return;
  }

  const completed = new Set(options.completedRouteIds ?? []);
  const bounds = L.latLngBounds([]);
  for (const feature of routeFeatures) extendBoundsWithFeature(bounds, feature);
  for (const door of doors) bounds.extend([door.lat, door.lng]);
  if (start) bounds.extend([start.lat, start.lng]);

  const map = L.map(element, {
    zoomControl: true,
    attributionControl: true,
    scrollWheelZoom: false
  });
  makeMapPageFriendly(map, element);

  if (bounds.isValid()) {
    map.fitBounds(bounds.pad(0.12), { maxZoom: 17 });
  } else if (fallback) {
    map.setView([fallback.lat, fallback.lng], 16);
  } else {
    map.setView(FALLBACK_CENTER, 15);
  }

  addReadableStreetBasemap(map, L);

  let routeLayer: any = null;
  if (routeFeatures.length) {
    routeLayer = L.geoJSON(
      {
        type: 'FeatureCollection',
        features: routeFeatures
      },
      {
        style(feature: RouteMapFeature | undefined) {
          return assignmentRouteStyle(feature, options, completed, 15);
        },
        onEachFeature(feature: RouteMapFeature, layer: any) {
          const routeId = feature.properties.routeId || 'Assigned section';
          const sectionLabel = options.sectionLabels?.[routeId] ?? routeId;
          const streets = feature.properties.streetNames?.join(', ') ?? '';
          const status = completed.has(routeId)
            ? 'Complete'
            : routeId === options.activeRouteId
              ? 'Current section'
              : 'Assigned';
          layer.bindPopup(
            `<strong>${escapeHtml(sectionLabel)}</strong><br>${escapeHtml(status)}${
              streets ? `<br>${escapeHtml(streets)}` : ''
            }`
          );
        }
      }
    ).addTo(map);
  }

  const doorLayer = L.layerGroup().addTo(map);
  const doorMarkers = doors.map((door) => ({ door, marker: addDoorMarker(doorLayer, door, options.onReportDoor) }));

  if (options.onReportDoor && doorMarkers.length) {
    map.on('click', (event: any) => {
      if (mapZoomBand(mapZoom(map)) !== 'CLOSE') return;
      const clickPoint = map.latLngToContainerPoint(event.latlng);
      let nearest: { marker: any; distance: number; hitRadius: number } | null = null;
      const mobile = window.matchMedia?.('(pointer: coarse)').matches === true || window.innerWidth < 760;
      const minimumRadius = mobile ? 46 : 30;
      const maximumRadius = mobile ? 92 : 62;

      for (const current of doorMarkers) {
        const point = map.latLngToContainerPoint([current.door.lat, current.door.lng]);
        const distance = point.distanceTo(clickPoint);
        let nearestNeighbor = Number.POSITIVE_INFINITY;
        for (const other of doorMarkers) {
          if (other === current) continue;
          const otherPoint = map.latLngToContainerPoint([other.door.lat, other.door.lng]);
          nearestNeighbor = Math.min(nearestNeighbor, point.distanceTo(otherPoint));
        }
        const lotRadius = Number.isFinite(nearestNeighbor) ? nearestNeighbor * 0.58 : maximumRadius;
        const hitRadius = Math.min(maximumRadius, Math.max(minimumRadius, lotRadius));
        if (distance <= hitRadius && (!nearest || distance < nearest.distance)) {
          nearest = { marker: current.marker, distance, hitRadius };
        }
      }
      nearest?.marker.openPopup?.();
    });
  }

  const startingDoor = start ? doorMarkers.find(({ door }) =>
    Math.abs(door.lat - start.lat) < 0.000001 && Math.abs(door.lng - start.lng) < 0.000001
  ) : undefined;
  const startMarker = startingDoor
    ? addDoorMarker(map, startingDoor.door, options.onReportDoor)
    : start
      ? L.circleMarker([start.lat, start.lng], {
          radius: 8, weight: 3, color: '#ffffff', fillColor: START_COLOR, fillOpacity: 1
        }).bindPopup('<strong>Suggested starting point</strong>').addTo(map)
      : null;
  if (startingDoor && startMarker) {
    startMarker.setRadius(8);
    startMarker.setStyle({ weight: 3 });
    startMarker.bindTooltip('Suggested starting point');
  }

  const routeIds = new Set(
    routeFeatures.map((feature) => feature.properties.routeId).filter(Boolean)
  );
  const summaryMarker = bounds.isValid() && (routeFeatures.length || doors.length)
    ? L.marker(bounds.getCenter(), {
        keyboard: true,
        riseOnHover: true,
        icon: L.divIcon({
          className: 'coverage-neighborhood-summary-icon',
          html: `<div class="coverage-neighborhood-summary coverage-neighborhood-summary--assignment"><strong>${routeIds.size.toLocaleString()} ${routeIds.size === 1 ? 'section' : 'sections'}</strong><span>${doors.length.toLocaleString()} doors · click to zoom in</span></div>`,
          iconSize: [0, 0],
          iconAnchor: [0, 0]
        })
      })
    : null;
  summaryMarker?.on('click', () => {
    if (bounds.isValid()) map.fitBounds(bounds.pad(0.12), { maxZoom: 17 });
  });

  const refreshZoomPresentation = (): void => {
    const zoom = mapZoom(map);
    const band = mapZoomBand(zoom);
    routeLayer?.setStyle?.((feature: RouteMapFeature | undefined) =>
      assignmentRouteStyle(feature, options, completed, zoom)
    );
    setGeoJsonInteractive(routeLayer, band !== 'FAR');

    if (band === 'CLOSE') {
      if (!map.hasLayer(doorLayer)) doorLayer.addTo(map);
    } else if (map.hasLayer(doorLayer)) {
      map.removeLayer(doorLayer);
      map.closePopup?.();
    }

    if (startMarker) {
      if (band === 'FAR' && map.hasLayer(startMarker)) map.removeLayer(startMarker);
      if (band !== 'FAR' && !map.hasLayer(startMarker)) startMarker.addTo(map);
    }

    if (summaryMarker) {
      if (band === 'FAR' && !map.hasLayer(summaryMarker)) summaryMarker.addTo(map);
      if (band !== 'FAR' && map.hasLayer(summaryMarker)) map.removeLayer(summaryMarker);
    }
  };

  map.on('zoomend', refreshZoomPresentation);
  refreshZoomPresentation();
  window.requestAnimationFrame(() => map.invalidateSize());
}

function assignmentRouteStyle(
  feature: RouteMapFeature | undefined,
  options: CoverageMapOptions,
  completed: Set<string>,
  zoom: number
): Record<string, unknown> {
  const routeId = feature?.properties.routeId ?? '';
  const isActive = Boolean(routeId && routeId === options.activeRouteId);
  const isComplete = completed.has(routeId);
  const color = isComplete ? COMPLETE_COLOR : isActive ? ROUTE_COLOR : OTHER_ROUTE_COLOR;
  const band = mapZoomBand(zoom);
  const weight = isActive
    ? zoomInterpolate(zoom, 2.4, 7.5)
    : zoomInterpolate(zoom, 1, 5.25);
  return {
    color,
    weight,
    opacity: band === 'FAR'
      ? (isActive ? 0.72 : 0)
      : band === 'MEDIUM'
        ? (isActive ? 0.88 : 0.48)
        : (isActive ? 0.95 : 0.78),
    lineCap: 'round',
    lineJoin: 'round'
  };
}

function setGeoJsonInteractive(layer: any, enabled: boolean): void {
  if (!layer) return;
  const apply = (child: any): void => {
    if (child?.options) child.options.interactive = enabled;
    const path = child?._path as SVGElement | undefined;
    if (path) path.style.pointerEvents = enabled ? 'auto' : 'none';
  };
  if (typeof layer.eachLayer === 'function') layer.eachLayer(apply);
  else apply(layer);
}

function extendBoundsWithFeature(bounds: any, feature: RouteMapFeature): void {
  if (feature.geometry.type === 'LineString') {
    extendBoundsWithLine(bounds, feature.geometry.coordinates);
    return;
  }
  for (const line of feature.geometry.coordinates) extendBoundsWithLine(bounds, line);
}

function extendBoundsWithLine(bounds: any, coordinates: Array<[number, number]>): void {
  for (const coordinate of coordinates) {
    const lng = Number(coordinate?.[0]);
    const lat = Number(coordinate?.[1]);
    if (Number.isFinite(lat) && Number.isFinite(lng)) bounds.extend([lat, lng]);
  }
}

function hasValidGeometry(feature: RouteMapFeature): boolean {
  if (!feature || feature.type !== 'Feature' || !feature.geometry) return false;
  if (feature.geometry.type === 'LineString') {
    return feature.geometry.coordinates.filter(isValidPosition).length >= 2;
  }
  return feature.geometry.coordinates.some((line) => line.filter(isValidPosition).length >= 2);
}

function isValidPosition(position: [number, number]): boolean {
  return Array.isArray(position) && position.length >= 2 &&
    Number.isFinite(Number(position[0])) && Number.isFinite(Number(position[1]));
}

function isValidDoor(door: DoorPoint): boolean {
  return Number.isFinite(Number(door?.lat)) && Number.isFinite(Number(door?.lng));
}

function isValidMapPoint(
  point: { lat: number; lng: number } | undefined
): point is { lat: number; lng: number } {
  return Boolean(point && Number.isFinite(Number(point.lat)) && Number.isFinite(Number(point.lng)));
}

function showMapFallback(element: HTMLDivElement, title: string, message: string): void {
  element.innerHTML = `
    <div class="map-empty-state">
      <strong>${escapeHtml(title)}</strong>
      <p>${escapeHtml(message)}</p>
    </div>
  `;
}

function addDoorMarker(
  layer: any,
  door: DoorPoint,
  onReportDoor?: (door: DoorPoint, conditionStatus: HouseConditionStatus | null) => Promise<void> | void
): any {
  const reported = Boolean(door.conditionStatus);
  const currentStatus = door.conditionStatus
    ? `<div class="door-report-current"><strong>${escapeHtml(conditionLabel(door.conditionStatus))}</strong>${
        door.conditionReportedBy ? `<small>Reported by ${escapeHtml(door.conditionReportedBy)}</small>` : ''
      }</div>`
    : '';
  const actions = onReportDoor ? `
    <div class="door-report-actions" aria-label="Report this house">
      <span>Mark this location:</span>
      ${reported ? '<small>Tap the selected status again to clear it.</small>' : ''}
      <button type="button" data-door-condition="EMPTY_LOT" aria-pressed="${door.conditionStatus === 'EMPTY_LOT'}">Empty lot</button>
      <button type="button" data-door-condition="UNDER_CONSTRUCTION" aria-pressed="${door.conditionStatus === 'UNDER_CONSTRUCTION'}">House under construction</button>
      <button type="button" data-door-condition="NO_SOLICITING" aria-pressed="${door.conditionStatus === 'NO_SOLICITING'}">No Soliciting</button>
      ${reported ? '<button type="button" data-door-condition="CLEAR">Clear status</button>' : ''}
      <small class="door-report-message" role="status"></small>
    </div>
  ` : '';
  const marker = L.circleMarker([door.lat, door.lng], {
    radius: reported ? 5 : 4,
    weight: 1.5,
    color: '#ffffff',
    fillColor: reported ? REPORTED_DOOR_COLOR : DOOR_COLOR,
    fillOpacity: 0.98
  })
    .bindPopup(
      `<div class="door-popup">${door.address ? `<strong>${escapeHtml(door.address)}</strong>` : '<strong>Assigned door</strong>'}${
        door.street ? `<br>${escapeHtml(door.street)}` : ''
      }${currentStatus}${actions}</div>`,
      { maxWidth: 290 }
    )
    .addTo(layer);

  if (!onReportDoor) return marker;
  marker.on('popupopen', (event: any) => {
    const popupElement = event.popup?.getElement?.() as HTMLElement | undefined;
    if (!popupElement) return;
    const buttons = Array.from(popupElement.querySelectorAll<HTMLButtonElement>('[data-door-condition]'));
    const message = popupElement.querySelector<HTMLElement>('.door-report-message');
    for (const button of buttons) {
      button.onclick = () => {
        const value = button.dataset.doorCondition;
        if (!value) return;
        const status = value === 'CLEAR' || value === door.conditionStatus ? null : value as HouseConditionStatus;
        buttons.forEach((candidate) => { candidate.disabled = true; });
        if (message) message.textContent = 'Saving…';
        Promise.resolve(onReportDoor(door, status)).catch((error) => {
          buttons.forEach((candidate) => { candidate.disabled = false; });
          if (message) message.textContent = error instanceof Error ? error.message : 'Could not save this report.';
        });
      };
    }
  });
  return marker;
}

function conditionLabel(status: HouseConditionStatus): string {
  if (status === 'EMPTY_LOT') return 'Empty lot';
  if (status === 'UNDER_CONSTRUCTION') return 'House under construction';
  return 'No Soliciting';
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>'"]/g, (character) => {
    const replacements: Record<string, string> = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      "'": '&#39;',
      '"': '&quot;'
    };
    return replacements[character] ?? character;
  });
}
